/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Registers an event attendee after successful PaymentModal card payment.
 * @module logic/payment/registerEventAttendee
 */

import { CONTENT_TYPE_JSON } from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
import {
  getProfileFieldValue,
  refreshToken,
  signOut,
} from "../../utils/common.js";
import dayjs from "dayjs";

/**
 * Builds the attendee POST body from PaymentModal registration form data.
 * @param {Object} formData
 * @param {Object} event
 * @returns {Object|null} Payload or null when required conditional fields are missing
 */
export const buildEventAttendeePayload = (formData, event) => {
  if (
    event.expectativasRequerido &&
    (formData.expectativas === null ||
      formData.expectativas === undefined ||
      formData.expectativas === "")
  ) {
    logger.error("Expectativas field is required");
    return null;
  }

  if (
    event.interesesComercialesRequerido &&
    (formData.interesesComerciales === null ||
      formData.interesesComerciales === undefined ||
      formData.interesesComerciales === "")
  ) {
    logger.error("Intereses comerciales field is required");
    return null;
  }

  const selectedProfileIndex = formData.perfilSeleccionado ?? 0;
  const ambitoOperacionesToSend = getProfileFieldValue(
    formData,
    "ambitoOperaciones",
    selectedProfileIndex,
    formData.ambitoOperaciones,
  );

  const { perfilSeleccionado, perfiles, ...restFormData } = formData;

  return {
    ...restFormData,
    ambitoOperaciones: ambitoOperacionesToSend,
    idEvento: event._id,
    idUsuario: localStorage.getItem("userId"),
    fechaInscripcion: dayjs().utc("z").toISOString(),
  };
};

/**
 * POSTs attendee registration to the API with token refresh retry on expiry.
 * @param {Object} options
 * @param {Object} options.attendanceData
 * @param {string} options.authToken
 * @param {import("react-router").NavigateFunction} options.navigate
 * @returns {Promise<boolean>} True when registration succeeded
 */
export const registerEventAttendee = async ({
  attendanceData,
  authToken,
  navigate,
}) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}attendee`,
      {
        method: "POST",
        headers: {
          "Content-Type": CONTENT_TYPE_JSON,
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(attendanceData),
      },
    );

    if (response.ok) {
      const responseData = await response.json();
      logger.info("Registration successful:", { data: responseData });
      return true;
    }

    const errorData = await response.json();
    if (errorData?.message === "The incoming token has expired") {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return false;
      }
      return registerEventAttendee({
        attendanceData,
        authToken: localStorage.getItem("token"),
        navigate,
      });
    }

    logger.error("Failed to register in event:", {
      data: [response.status, errorData.message],
    });
    return false;
  } catch (error) {
    logger.error("Error:", { err: sanitizeError(error) });
    return false;
  }
};
